import HiddenInput from "@/components/form/components/HiddenInput";
import AntIcon from "@/components/icons/AntIcon";
import { selectStandardProps, selectTagsStandardProps } from "@/utils/componentUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, Flex, Form, Input, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../../common/constants";
import { useIsCurrentAgentTypePlus } from "../../../../../auth/hooks";
import InstitutionSelect from "../../../../../enumerations/components/form/InstitutionSelect";
import {
  CommissionOriginType,
  CommissionProcessingResult,
  CommissionsBatchStep,
  PostponementReason,
  postponementReasonTMap,
} from "../../../enums";
import { CommissionsFilterPageRequest } from "../../../types";
import PostponementReasonTag from "../../PostponementReasonTag";

interface Props {
  showCalculationFilterOptions?: boolean;
  onFilterSubmit: (filter: CommissionsFilterPageRequest) => void;
  step: CommissionsBatchStep;
  filterPageSize: number;
}

export const BatchCalculatedCommissionFilterForm = ({
  showCalculationFilterOptions,
  onFilterSubmit,
  step,
  filterPageSize,
}: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const [form] = Form.useForm<CommissionsFilterPageRequest>();
  const processingResult = Form.useWatch("processingResult", form);

  const handleProcessingResultChange = (processingResult: CommissionProcessingResult) => {
    if (processingResult !== CommissionProcessingResult.POSTPONED) {
      form.setFieldsValue({ postponementReasons: [] });
    }
  };

  useEffect(() => {
    form.resetFields();
  }, [step]);

  return (
    <Card title={t("commissions.batch.filter.title")} size="small" style={{ marginBottom: 24 }}>
      <Form form={form} layout="vertical" onFinish={onFilterSubmit} name="calculatedCommissionFilterForm">
        <HiddenInput name="pageSize" initialValue={filterPageSize} />

        <Flex align="end" gap={rowGutter}>
          <Form.Item
            style={{ flex: 1 }}
            name="keyword"
            label={t("common.searchKeyword")}
            rules={[
              validations.size(
                validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
              ),
            ]}
          >
            <Input allowClear placeholder={t("commissions.batch.helpers.commissionsSearchHint")} />
          </Form.Item>

          <Form.Item
            style={{ flex: 1 }}
            name="origin"
            label={t("commissions.batch.enums.commissionOriginType._label")}
            rules={[validations.none]}
            initialValue={CommissionOriginType.ALL}
          >
            <Select
              {...selectStandardProps}
              options={[
                CommissionOriginType.ALL,
                CommissionOriginType.CURRENT_BATCH,
                CommissionOriginType.PREVIOUS_BATCHES,
                isTopAgentPlus ? CommissionOriginType.SPLIT_ADDED : undefined,
              ]
                .filter((item) => !!item)
                .map((origin) => ({
                  value: origin,
                  label: t("commissions.batch.enums.commissionOriginType." + origin),
                }))}
            />
          </Form.Item>

          <InstitutionSelect
            formItemProps={{
              name: "institutionIds",
              label: t("common.institutions"),
              rules: [validations.none],
              style: { flex: 1 },
            }}
            selectProps={{ mode: "multiple", maxTagCount: "responsive", allowClear: true }}
            optionsProps={{ groupByType: true }}
          />

          <Form.Item
            style={{ flex: 1 }}
            name="processingResult"
            label={t("commissions.batch.enums.commissionProcessingResult._label")}
            rules={[validations.none]}
            initialValue={CommissionProcessingResult.ALL}
          >
            <Select
              {...selectStandardProps}
              options={(showCalculationFilterOptions
                ? [
                    CommissionProcessingResult.ALL,
                    CommissionProcessingResult.PROCESSED,
                    CommissionProcessingResult.WITH_UNMODIFIED_CALCULATION,
                    CommissionProcessingResult.WITH_MODIFIED_CALCULATION,
                    CommissionProcessingResult.WITH_CALCULATION_ERROR,
                    CommissionProcessingResult.POSTPONED,
                  ]
                : [
                    CommissionProcessingResult.ALL,
                    CommissionProcessingResult.PROCESSED,
                    CommissionProcessingResult.POSTPONED,
                  ]
              ).map((processingResult) => ({
                value: processingResult,
                label: t("commissions.batch.enums.commissionProcessingResult." + processingResult),
              }))}
              onChange={handleProcessingResultChange}
            />
          </Form.Item>

          <Form.Item
            style={{ flex: 1 }}
            name="postponementReasons"
            label={t("commissions.batch.enums.postponementReason._label")}
            rules={[validations.none]}
          >
            <Select
              {...selectTagsStandardProps(postponementReasonTMap)}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
              disabled={processingResult !== CommissionProcessingResult.POSTPONED}
              tagRender={(props) => (
                <PostponementReasonTag
                  reason={props.value}
                  closable={props.closable}
                  onClose={props.onClose}
                  label={props.label}
                />
              )}
              options={Object.values(PostponementReason).map((reason) => ({
                value: reason,
                label: <PostponementReasonTag reason={reason} />,
              }))}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
              {t("common.filter")}
            </Button>
          </Form.Item>
        </Flex>
      </Form>
    </Card>
  );
};
